import React, { useEffect, useCallback } from "react";
import { Form, Input, Button, Row, Col, Select } from "antd";
import { FormStepWrapper } from "./AddTalentProfile.style";
import RoleSelectWithOther from "../../../../components/RoleSelectWithOther";
import useLocationOptions from "../../../../hooks/useLocationOptions";

const { Option } = Select;

const skillOptions = [
  "React",
  "Angular",
  "Vue.js",
  "Node.js",
  "Python",
  "Django",
  "Flask",
  "Java",
  "Spring Boot",
  "PHP",
  "JavaScript",
  "TypeScript",
  "HTML/CSS",
  "SAP HANA",
  "SQL",
  "PostgreSQL",
  "MongoDB",
  "Docker",
  "Kubernetes",
  "AWS",
  "Azure",
  "GCP",
  "Git",
  "CI/CD",
  "Agile",
  "Scrum",
  "GraphQL",
  "REST API",
  "Redis",
  "Elasticsearch",
];

const BasicInformation = ({ initialData, onNext, onCancel, isFirstStep, isEditMode, partnerOrganizations = [], loading }) => {
  const [form] = Form.useForm();

  const { countries, cities, setSelectedCountry } = useLocationOptions(initialData?.country || "India");

  useEffect(() => {
    if (initialData && initialData.name) {
      form.setFieldsValue({ country: "India", ...initialData });
    }
  }, [initialData, form]);

  const handleCountryChange = useCallback((value) => {
    setSelectedCountry(value);
    form.setFieldValue("location", undefined);
  }, [setSelectedCountry, form]);

  const handleNext = () => {
    form
      .validateFields()
      .then((values) => {
        onNext(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <FormStepWrapper>
      <h2 className="form-title">Basic Information</h2>
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{ country: "India", ...initialData }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Partner Organization"
              name="partnerOrganization"
              rules={[
                { required: true, message: "Please select a partner organization!" },
              ]}
            >
              <Select 
                placeholder="Select Partner Organization"
                showSearch
                optionFilterProp="children"
              >
                {partnerOrganizations.map((partner) => (
                  <Option key={partner.id} value={partner.name}>
                    {partner.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the talent name!" },
              ]}
            >
              <Input placeholder="Enter Full Name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: "Please select a role!" }]}
            >
              <RoleSelectWithOther source="admin" placeholder="Select Role" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Experience">
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item 
                    name="experienceYears" 
                    noStyle
                    rules={[
                      { required: true, message: "Years required!" },
                    ]}
                  >
                    <Input placeholder="Years" type="number" min="0" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item 
                    name="experienceMonths" 
                    noStyle
                    rules={[
                      { required: true, message: "Months required!" },
                    ]}
                  >
                    <Input placeholder="Months" type="number" min="0" max="11" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Primary Skills" required>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
            <Form.Item
              name={["primarySkills", 0]}
              rules={[{ required: true, message: "Please select primary skill 1" }]}
              noStyle
            >
              <Select placeholder="Primary Skill 1" showSearch optionFilterProp="children">
                {skillOptions.map((skill) => (
                  <Option key={skill} value={skill}>
                    {skill}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name={["primarySkills", 1]} noStyle>
              <Select placeholder="Primary Skill 2" showSearch optionFilterProp="children" allowClear>
                {skillOptions.map((skill) => (
                  <Option key={skill} value={skill}>
                    {skill}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name={["primarySkills", 2]} noStyle>
              <Select placeholder="Primary Skill 3" showSearch optionFilterProp="children" allowClear>
                {skillOptions.map((skill) => (
                  <Option key={skill} value={skill}>
                    {skill}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          name="secondarySkills"
          label="Secondary Skills"
        >
          <Select
            mode="tags"
            placeholder="Select or add secondary skills"
            showSearch
            allowClear
          >
            {skillOptions.map((skill) => (
              <Option key={skill} value={skill}>
                {skill}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Monthly Rate" 
              name="rate"
              rules={[
                { required: true, message: "Please enter monthly rate!" },
              ]}
            >
              <Input placeholder="Enter Monthly Rate" prefix="₹" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              label="Notice Period" 
              name="notice"
              rules={[
                { required: true, message: "Please enter notice period!" },
              ]}
            >
              <Input placeholder="e.g., 30 days" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Email" 
              name="email"
              rules={[
                { required: true, message: "Please enter email!" },
                { type: "email", message: "Please enter valid email!" },
              ]}
            >
              <Input placeholder="Enter email address" type="email" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              label="Phone" 
              name="phone"
              rules={[
                { required: true, message: "Please enter phone number!" },
              ]}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: "Please select country!" }]}
            >
              <Select
                placeholder="Select Country"
                onChange={handleCountryChange}
                showSearch
                optionFilterProp="children"
              >
                {countries.map((c) => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="City / Location"
              name="location"
              rules={[{ required: true, message: "Please select location!" }]}
            >
              <Select placeholder="Select City" showSearch optionFilterProp="children">
                {cities.map((c) => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Availability" 
              name="availability"
              rules={[
                { required: true, message: "Please select availability!" },
              ]}
            >
              <Select placeholder="Select Availability">
                <Option value="Immediately Available">Immediately Available</Option>
                <Option value="Available in 2 weeks">Available in 2 weeks</Option>
                <Option value="Available in 1 month">Available in 1 month</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="button-group">
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        </div>
      </Form>
    </FormStepWrapper>
  );
};

export default BasicInformation;
